import type { PropsWithChildren } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { generatePath, useLocation, useNavigate, useParams } from 'react-router-dom-v5-compat';

import config from '@/config';
import { useAppSelector } from '@/hooks/useAppSelector';
import { getListVaultsQueryKey } from '@/modules/api/codegen';
import { Form, handleFormSubmit, useTranslateSubmitError } from '@/modules/forms';

import { Fields, SecretType, TOTP_VALUE_PLACEHOLDER } from '../../constants';
import { secretFormSchema, type SecretFormSchemaType, type SecretFormValues } from '../../schemas';
import { updateSecret } from '../../services/actions';
import { selectUpdateSecretFormInitialValues } from '../../services/selectors';
import * as felaRules from './UpdateSecretFormProvider.rules';

export interface UpdateSecretFormProviderProps extends PropsWithChildren {}

export const UpdateSecretFormProvider = ({ children }: UpdateSecretFormProviderProps) => {
    const { id } = useParams<{
        id: string;
    }>();

    if (!id) {
        throw new Error('Missing secret ID');
    }

    const { state } = useLocation();
    const navigate = useNavigate();

    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    const defaultValues = useAppSelector(state =>
        selectUpdateSecretFormInitialValues(state, {
            id,
        }),
    );

    const translateSubmitError = useTranslateSubmitError<SecretFormValues>();

    return (
        <Form<SecretFormSchemaType, SecretFormValues>
            defaultValues={defaultValues}
            schema={secretFormSchema}
            onSubmit={async (values, { setError, setValue }) => {
                if (values.type === SecretType.Password) {
                    // Do not keep secret key, replace it with placeholder and send value to backend
                    setValue(Fields.TOTP, values.TOTP ? TOTP_VALUE_PLACEHOLDER : null);
                }

                const error = await handleFormSubmit<SecretFormValues>((values, actions) => {
                    dispatch(updateSecret(id, values, actions));
                })(values);

                if (error) {
                    const translatedError = translateSubmitError(...error);
                    setError(...translatedError);
                    return;
                }

                // The saga owns the request, so the vault counts are refreshed here, where the result lands.
                queryClient.invalidateQueries({ queryKey: getListVaultsQueryKey() });

                if (state?.from) {
                    navigate(state.from);
                } else {
                    navigate(generatePath(config.routes.secret, { id }));
                }
            }}
            extend={{ form: felaRules.form }}
        >
            {children}
        </Form>
    );
};
